import { DateRangePickers } from '@/components/common/DateRangePickers';
import { usePharmacistReplenishmentOrders } from '@/context/replenishment-orders/PharmacistReplenishmentOrders';
import type { ReplenishmentOrderItemDto } from '@montugroup/circuit-api-contracts';
import { Box, Chip, Link, PaginationLabelVariant, PaginationVariant, Stack, Table } from '@montugroup/design-system';
import type { Theme } from '@mui/material';
import { debounce, styled } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

const MIN_TABLE_HEIGHT = 400;

const StatusChip = styled(Chip)(() => ({
  size: 'medium',
  variant: 'outlined',
  fontWeight: 500,
}));

function ReplenishmentOrderItemsTable() {
  const {
    replenishmentOrderItems,
    loading,
    handleSortingOrderChange,
    shouldResetPageIndex,
    pageSize,
    handlePaginationModelChange,
    beforeCursor,
    afterCursor,
    endDateFilter,
    setEndDateFilter,
    startDateFilter,
    setStartDateFilter,
  } = usePharmacistReplenishmentOrders();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(MIN_TABLE_HEIGHT);

  const getItemStatus = (row: ReplenishmentOrderItemDto) => {
    if (row.fulfilledQuantity > 0) {
      if (row.fulfilledQuantity === row.quantity) {
        return (
          <StatusChip
            label="Received"
            color="success"
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.success.main + '15',
              color: theme.palette.success.main,
            })}
          />
        );
      }
      return (
        <StatusChip
          label="Partially Received"
          color="warning"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.secondary.main + '15',
            color: theme.palette.secondary.main,
          })}
        />
      );
    } else if (row.consignedQuantity > 0) {
      return (
        <StatusChip
          label="Processing"
          color="primary"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.primary.main + '15',
            color: theme.palette.primary.main,
          })}
        />
      );
    } else if (row.cancelledQuantity > 0) {
      if (row.cancelledQuantity === row.quantity) {
        return (
          <StatusChip
            label="Cancelled"
            color="error"
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.error.main + '15',
              color: theme.palette.error.main,
            })}
          />
        );
      }
      return (
        <StatusChip
          label="Partially Cancelled"
          color="error"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.warning.main + '15',
            color: theme.palette.warning.main,
          })}
        />
      );
    }
    return (
      <StatusChip
        label="Pending"
        color="info"
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.info.main + '15',
          color: theme.palette.info.main,
        })}
      />
    );
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;

        const FOOTER_HEIGHT = 155; // px
        const availableHeight = viewportHeight - containerTop - FOOTER_HEIGHT;

        setContainerHeight(Math.max(availableHeight, MIN_TABLE_HEIGHT));
      }
    };

    handleResize();
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  }, []);

  const columns: ColumnDef<ReplenishmentOrderItemDto>[] = useMemo(() => {
    return [
      {
        accessorKey: 'replenishmentOrderId',
        header: 'Order ID',
        enableSorting: true,
        cell: ({ row }) => <Box>{row.original.replenishmentOrderId} </Box>,
      },
      {
        accessorKey: 'product.name',
        header: 'Product Name',
        enableSorting: false,
        cell: ({ row }) => <Box>{row.original.product.name}</Box>,
      },
      {
        accessorKey: 'product.sku',
        header: 'Product SKU',
        enableSorting: false,
        cell: ({ row }) => <Box>{row.original.product.sku}</Box>,
      },
      {
        accessorKey: 'id',
        header: 'Status',
        meta: {
          sx: { textAlign: 'center' },
        },
        cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {getItemStatus(row.original)}
          </Box>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: 'Order Date',
        cell: ({ row }) => <Box>{new Date(row.original.createdAt).toLocaleDateString()}</Box>,
        enableSorting: true,
      },
      {
        accessorKey: 'quantity',
        header: 'Ordered Qty',
        cell: ({ row }) => <Box>{row.original.quantity}</Box>,
        enableSorting: false,
      },
      {
        accessorKey: 'cancelledQuantity',
        header: 'Cancelled Qty',
        cell: ({ row }) => <Box>{row.original.cancelledQuantity}</Box>,
        enableSorting: false,
      },
      {
        accessorKey: 'consignedQuantity',
        header: 'Processing Qty',
        cell: ({ row }) => <Box>{row.original.consignedQuantity}</Box>,
        enableSorting: false,
      },
      {
        accessorKey: 'fulfilledQuantity',
        header: 'Shipped Qty',
        cell: ({ row }) => <Box>{row.original.fulfilledQuantity}</Box>,
        enableSorting: false,
      },
      {
        accessorKey: 'receivedQuantity',
        header: 'Received Qty',
        cell: ({ row }) => <Box>{row.original.receivedQuantity}</Box>,
        enableSorting: false,
      },
      {
        accessorKey: 'approval.code',
        header: 'Approval ID',
        cell: ({ row }) =>
          row.original.approval ? (
            <Link id="b2b_table_cell_document_link" to={row.original.approval.documentPath}>
              {row.original.approval.code}
            </Link>
          ) : (
            ''
          ),
        enableSorting: false,
      },
    ];
  }, []);

  return (
    <Box>
      <Stack direction={{ sm: 'column', md: 'row' }} spacing={4} alignItems="center" pb={4}>
        <Stack direction={{ sm: 'column', md: 'row' }} gap={4}>
          <DateRangePickers
            startDate={startDateFilter}
            setStartDate={setStartDateFilter}
            endDate={endDateFilter}
            setEndDate={setEndDateFilter}
          />
        </Stack>
      </Stack>
      <Box ref={containerRef}>
        <Table
          data={replenishmentOrderItems}
          columns={columns}
          isLoading={loading}
          showPagination
          manualPagination
          shouldResetPageIndex={shouldResetPageIndex}
          pageSize={pageSize}
          hasNextPage={afterCursor !== null}
          hasPrevPage={beforeCursor !== null}
          paginationLabelVariant={PaginationLabelVariant.NONE}
          paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
          onPaginationModelChange={handlePaginationModelChange}
          onSortingChange={handleSortingOrderChange}
          isScrollable
          maxHeight={containerHeight}
        />
      </Box>
    </Box>
  );
}

export default ReplenishmentOrderItemsTable;
